import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid'
import { useTranslation } from '../../Translation';
import { useState, useEffect } from 'react';
import { bgColor, focusVisible, hoverBgColor, hoverTextColor, textColor } from '../../Resources/CSS/styling';
import { AdjustmentsHorizontalIcon, EllipsisHorizontalCircleIcon, EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import Pagination from './Pagination';
import Dropdown from '../Dropdown';
import Badge from '../Badge';

const people = [
    { name: 'Lindsay Walton', title: 'Front-end Developer', email: 'lindsay.walton@example.com', role: 'Member' },
    // More people...
]

export function useColumns(data, columnsPassed, onSort, activeSortColumn) {
    const [columns, setColumns] = useState([]);

    useEffect(() => {
        if (data && data.length > 0 && !columnsPassed) {
            setColumns(Object.keys(data[0]).map((key) => ({
                key: key,
                ordered: key === activeSortColumn
            })));
        } else if (columnsPassed) {
            setColumns(columnsPassed.map((column) => {
                const columnKey = typeof column === 'object' ? column.key : column;
                return {
                    key: columnKey,
                    type: typeof column === 'object' ? column.type : null,
                    label: typeof column === 'object' ? column.label : null,
                    render: typeof column === 'object' ? column.render : null,
                    color: typeof column === 'object' ? column.color : null,
                    ordered: columnKey === activeSortColumn
                };
            }));
        }
    }, [data, activeSortColumn]);

    const setColumnOrdered = (column) => {
        setColumns(columns.map((c) => ({
            ...c,
            ordered: c.key === column.key
        })));
        
        if (onSort) {
            onSort({ key: column.key });
        }
    }

    return { columns, setColumnOrdered };
}

export const renderColumn = (column, index, row) => {


    if (column.type === 'badge') {
        return <Badge withoutRemove={true} color={column.color ? column.color(row) : 'gray'} text={row[column.key]} />;
    } else if (column.type === 'custom') {
        return column.render(row);
    }
    return row[column.key]

}

export default function Table( props) {
    const { title, description, createButton, data, translationKey, dropdownGroups, actions, filterButton, onSort, pagination } = props;
    const columnsPassed = props.columns;

    const activeSortColumn = pagination?.sortBy;
    const activeSortOrder = pagination?.sortOrder;

    const { columns, setColumnOrdered } = useColumns(data, columnsPassed, onSort, activeSortColumn);
    const { translate } = useTranslation();

    return (
        <div className="">
            <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                    <h1 className="text-base font-semibold text-gray-900">{title}</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        {description}
                    </p>
                </div>
                <div className="mt-4 sm-ml-16 sm:mt-0 sm:flex-none flex-row flex gap-x-4">
                    {actions && actions.length > 0 && <div className="flex flex-row">
                        {actions.map((action, index) => (
                            <button
                                key={'action-' + translate(translationKey + '.' + action.text) + index}
                                onClick={action.action}
                                type="button"
                                className={`ml-3 block rounded-md ${bgColor} px-3 py-2 text-center text-sm font-semibold text-white shadow-sm ${hoverBgColor} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${focusVisible}`}
                            >
                                {action.text}
                            </button>
                        ))}
                    </div>}
                    {createButton && <button
                        onClick={createButton.action}
                        type="button"
                        className={`block rounded-md ${bgColor} px-3 py-2 text-center text-sm font-semibold text-white shadow-sm ${hoverBgColor} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${focusVisible}`}
                    >
                        {createButton.text}
                    </button>}
                    {filterButton && <button
                        onClick={filterButton.action}
                        type="button"
                        className={`flex flex-row items-center gap-x-2 rounded-md bg-white px-3 py-2 text-center text-sm border font-semibold text-gray-900 shadow-sm hover:bg-gray-50 border-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${focusVisible}`}
                    >
                        <AdjustmentsHorizontalIcon className="w-5 h-5" />
                        {filterButton.text}
                    </button>}
                </div>
            </div>
           
            <div className="mt-8 flow-root">
                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                        <table className="min-w-full divide-y divide-gray-300">
                            <thead>
                                <tr>
                                    {columns.map((column) => (
                                        <th key={column.key} scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                                            <a className="group inline-flex cursor-pointer" onClick={() => setColumnOrdered(column)}>
                                                {column.label || translate(translationKey + '.' + column.key)}
                                                <span className={`${column.ordered && column.key === activeSortColumn ? 'visible ml-2 flex-none rounded bg-gray-100 text-gray-900 group-hover:bg-gray-200' : 'invisible ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible'}`}>
                                                    {column.ordered && column.key === activeSortColumn && activeSortOrder === 'desc' ? <ChevronDownIcon aria-hidden="true" className="size-5" /> : <ChevronUpIcon aria-hidden="true" className="size-5" />}
                                                </span>
                                            </a>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                                {data.map((item) => (
                                    <tr key={item.key || item.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => {
                                        if (props.onRowClick) {
                                            props.onRowClick(item);
                                        }
                                    }}>
                                        {columns.map((column, index) => (
                                            <td className={index === 0 ? 'whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0' : 'whitespace-nowrap px-3 py-4 text-sm text-gray-500'}>
                                                {renderColumn(column, index, item)}
                                            </td>
                                            
                                        ))}
                                        {dropdownGroups &&  dropdownGroups.length > 0 && <td className="flex flex-row whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm sm:pr-0 items-end justify-end">
                                            <Dropdown row={item} groups={dropdownGroups}/>
                                        </td>}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div>
                {pagination && <Pagination pagination={pagination} />}
            </div>
        </div>
    )
}
